import React, { useCallback, useEffect, useRef, useState } from 'react';
import { icons } from '~/assets';
import ContainerFlex from '../containerFlex';

import * as St from './styles';
import Spinner from '../spinner';
import THEMES from '~/themes/themes';

interface IProps {
  title: string | JSX.Element;
  titlePadding?: number;
  titleColor?: string;
  initialState?: 'open' | 'close';
  onCollapse?: any;
  collapseHeight?: number;
  actionOnOpen?: Function;
  callFunctionOnExpand?: Function;
  onBackEvent?: boolean;
  waitRequest?: boolean;
  disable?: boolean;
  typeEvent?: boolean;
  changeProduct?: boolean;
}

const Collapse: React.FC<IProps> = ({
  children,
  title,
  titlePadding,
  titleColor,
  initialState,
  onCollapse,
  collapseHeight,
  actionOnOpen,
  callFunctionOnExpand,
  onBackEvent = true,
  waitRequest,
  disable,
  typeEvent,
  changeProduct,
}) => {
  const [contentIsOpen, setContentIsOpen] = useState(false);
  const [hasExpanded, setHasExpanded] = useState(false);

  useEffect(() => {
    if (initialState === 'open' && !disable) {
      setContentIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (contentIsOpen) {
      toggleContent();
    }
  }, [changeProduct]);

  useEffect(() => {
    if (!onBackEvent && typeEvent) {
      setContentIsOpen(false);
    }
  }, [onBackEvent]);

  const contentRef = useRef<HTMLDivElement>();
  const wrapperChildrenRef = useRef<HTMLDivElement>();
  const toggleContent = () => setContentIsOpen((prev) => !prev);
  const setContentHeight = useCallback(() => {
    if (!contentRef.current || !wrapperChildrenRef.current) return;

    const wrapperChildrenHeight = wrapperChildrenRef.current.clientHeight;

    contentRef.current.style.height = collapseHeight
      ? `${wrapperChildrenHeight + collapseHeight}px`
      : `${wrapperChildrenHeight}px`;
  }, [collapseHeight]);

  useEffect(() => {
    if (!typeEvent) {
      if (contentIsOpen && !disable) {
        setContentHeight();
        actionOnOpen && actionOnOpen();
      } else {
        contentRef.current.style.height = '0px';
      }
      onCollapse && onCollapse(contentIsOpen);
    }
  }, [contentIsOpen, collapseHeight, setContentHeight]);

  useEffect(() => {
    if (typeEvent) {
      if (contentIsOpen) {
        contentRef.current.style.height = '0px';
      }
      if (contentIsOpen && !disable && !waitRequest) {
        setContentHeight();
        actionOnOpen && actionOnOpen();
      } else {
        contentRef.current.style.height = '0px';
      }
      onCollapse && onCollapse(contentIsOpen);
    }
  }, [contentIsOpen, collapseHeight, waitRequest, setContentHeight]);

  useEffect(() => {
    if (!contentIsOpen || disable || !wrapperChildrenRef.current) return;

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      setContentHeight();
    });

    observer.observe(wrapperChildrenRef.current);

    return () => observer.disconnect();
  }, [contentIsOpen, disable, setContentHeight]);

  return (
    <St.Container>
      <ContainerFlex
        color={titleColor || '#fbfbfb'}
        padding={titlePadding || 26}
        onClick={() => {
          !disable
            ? (toggleContent(),
              !contentIsOpen &&
                typeof callFunctionOnExpand === 'function' &&
                callFunctionOnExpand())
            : null;
        }}
        style={{ cursor: !disable ? 'pointer' : 'default' }}
        row
        horizontal
        center
        space
      >
        <St.TitleCategory>{title}</St.TitleCategory>
        {waitRequest ? (
          <Spinner color="black" />
        ) : (
          <St.Icon
            src={icons.arrowBlack}
            alt="arrow black"
            width={35}
            open={contentIsOpen}
          />
        )}
      </ContainerFlex>
      <St.Content ref={contentRef} open={contentIsOpen}>
        <div ref={wrapperChildrenRef}>{children}</div>
      </St.Content>
    </St.Container>
  );
};

export default Collapse;
